import React, { useState } from "react";
import { IoChevronBack } from "react-icons/io5";
import { FiUser, FiClock, FiAlertCircle, FiX } from "react-icons/fi";

interface PendingProductDetailsProps {
  product: any;
  onBack: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string, rejectionReason: string) => void;
  onEdit: (product: any) => void;
  adminLevel: string | null;
}

// ── Helpers ────────────────────────────────────────────────────────────

const getSubmittedByName = (submittedBy: any): string => {
  if (!submittedBy) return "Unknown Admin";
  if (typeof submittedBy === "string") return submittedBy;
  return submittedBy.name || submittedBy.email || "Unknown Admin";
};

const getSubmittedByEmail = (submittedBy: any): string | null => {
  if (typeof submittedBy === "object" && submittedBy?.email) return submittedBy.email;
  return null;
};

const hasChanged = (original: any, proposed: any): boolean => {
  if (proposed === undefined) return false;
  const a = original ?? "";
  const b = proposed ?? "";
  if (typeof a === "number" || typeof b === "number") return Number(a) !== Number(b);
  return String(a) !== String(b);
};

const formatPrice = (price: any): string =>
  typeof price === "number" ? `₦${price.toLocaleString()}` : "--";

const getStatusBadge = (status: string) => {
  switch (status?.toLowerCase()) {
    case "approved":
      return { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Approved" };
    case "rejected":
      return { bg: "bg-red-50 text-red-600 border-red-200", label: "Rejected" };
    case "under-review":
      return { bg: "bg-purple-50 text-purple-600 border-purple-200", label: "Under Review" };
    case "pending":
    default:
      return { bg: "bg-amber-50 text-amber-600 border-amber-200", label: "Pending" };
  }
};

/** Whether this product can still be actioned (approve/reject).
 *  Only hide actions for products that have already been definitively decided. */
const isActionable = (status: string): boolean => {
  const s = status?.toLowerCase();
  return s !== "approved" && s !== "rejected";
};

// ── Component ──────────────────────────────────────────────────────────

const PendingProductDetails: React.FC<PendingProductDetailsProps> = ({
  product,
  onBack,
  onApprove,
  onReject,
  onEdit,
  adminLevel,
}) => {
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  const previous = product.previousData ?? null;
  const proposed = product.proposedData ?? null;
  const isUpdate = product.changeType === "update" || previous !== null;

  const effectiveProposed = isUpdate && previous
    ? { ...previous, ...proposed }
    : product;

  const handleApprove = async () => {
    setApproving(true);
    try {
      await onApprove(product._id);
    } finally {
      setApproving(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) return;
    setRejecting(true);
    try {
      await onReject(product._id, rejectionReason.trim());
    } finally {
      setRejecting(false);
      setShowRejectModal(false);
      setRejectionReason("");
    }
  };

  const comparisonFields: { label: string; key: string; format?: (v: any) => string }[] = [
    { label: "Product Name", key: "name" },
    { label: "Category", key: "type" },
    { label: "Price", key: "price", format: formatPrice },
    { label: "Measurement", key: "measurement" },
    { label: "Quantity", key: "quantity", format: (v) => (v != null ? String(v) : "--") },
    { label: "Description", key: "description" },
  ];

  const highlightClass = "bg-amber-50/50 border-l-[3px] border-amber-400 pl-2 -ml-[3px] py-0.5 rounded-r";
  const statusBadge = getStatusBadge(product.status);
  const submittedByName = getSubmittedByName(product.submittedBy);
  const submittedByEmail = getSubmittedByEmail(product.submittedBy);
  const canAction = isActionable(product.status);

  return (
    <>
      <div className="bg-white rounded-lg px-5 py-4 w-full">
        {/* ── Header ──────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div
            className="flex items-center gap-1.5 text-gray-500 cursor-pointer hover:text-secondary transition-colors"
            onClick={onBack}
          >
            <IoChevronBack className="w-4 h-4" />
            <span className="font-semibold text-sm">
              View Product Details
              <span className="text-gray-300 font-normal mx-1">/</span>
              <span className="text-gray-400 font-normal">{product.name}</span>
            </span>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            <span
              className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide border ${isUpdate
                ? "bg-amber-50 text-amber-600 border-amber-200"
                : "bg-blue-50 text-blue-600 border-blue-200"
                }`}
            >
              {isUpdate ? "Update" : "New"}
            </span>

            <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide border ${statusBadge.bg}`}>
              {statusBadge.label}
            </span>

            <div className="w-px h-3.5 bg-gray-200 hidden sm:block" />

            <div className="flex items-center gap-1 text-xs text-gray-500">
              <FiUser className="w-3 h-3" />
              <span className="font-medium text-gray-700">{submittedByName}</span>
              {submittedByEmail && (
                <span className="text-gray-400 hidden sm:inline">({submittedByEmail})</span>
              )}
            </div>

            {product.updatedAt && (
              <>
                <div className="w-px h-3.5 bg-gray-200 hidden sm:block" />
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <FiClock className="w-3 h-3" />
                  <span>
                    {new Date(product.updatedAt).toLocaleDateString("en-NG", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Rejection Reason Banner ─────────────────────────── */}
        {product.status?.toLowerCase() === "rejected" && product.rejectionReason && (
          <div className="flex items-start gap-2 mb-4 rounded border border-red-200 bg-red-50/50 px-4 py-2.5">
            <FiAlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-red-700 mb-0.5">Rejection Reason</p>
              <p className="text-xs text-red-600 leading-relaxed">{product.rejectionReason}</p>
            </div>
          </div>
        )}

        {/* ── Main Content ─────────────────────────────────────── */}
        {isUpdate && previous ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* ── Left: Current Product ──────────────────────── */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Current State
                </h3>
              </div>
              <div className="p-4">
                <div className="bg-[#FCFCFC] border border-gray-100 rounded-md flex items-center justify-center p-3 mb-4 min-h-[120px]">
                  <img
                    src={previous.image || product.image || "/placeholder-image.png"}
                    alt={previous.name || product.name}
                    className="max-w-full max-h-[100px] object-contain"
                  />
                </div>
                <div className="grid grid-cols-2 gap-y-2.5 gap-x-4">
                  {comparisonFields.map(({ label, key, format }) => (
                    <div key={key}>
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                        {label}
                      </p>
                      <p className="text-xs text-gray-700 font-medium">
                        {format ? format(previous[key]) : previous[key] || "--"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Right: Proposed Changes ────────────────────── */}
            <div className="border border-secondary/30 bg-secondary/[0.02] rounded-lg overflow-hidden ring-1 ring-secondary/5">
              <div className="bg-secondary/10 px-4 py-2 border-b border-secondary/20">
                <h3 className="text-xs font-bold text-secondary uppercase tracking-wider">
                  Proposed State
                </h3>
              </div>
              <div className="p-4">
                <div
                  className={`rounded-md flex items-center justify-center p-3 mb-4 min-h-[120px] relative transition-colors ${hasChanged(previous.image, proposed?.image)
                    ? "bg-amber-50/50 border border-amber-200"
                    : "bg-[#FCFCFC] border border-gray-100"
                    }`}
                >
                  <img
                    src={effectiveProposed.image || product.image || product.images?.[0] || "/placeholder-image.png"}
                    alt={effectiveProposed.name || product.name}
                    className="max-w-full max-h-[100px] object-contain"
                  />
                  {hasChanged(previous.image, proposed?.image) && (
                    <span className="absolute top-1 right-1 text-[8px] font-bold uppercase bg-amber-400 text-amber-900 px-1.5 py-[1px] rounded shadow-sm">
                      Changed
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-y-2.5 gap-x-4">
                  {comparisonFields.map(({ label, key, format }) => {
                    const changed = hasChanged(previous[key], proposed?.[key]);
                    return (
                      <div key={key} className={changed ? highlightClass : ""}>
                        <div className="flex items-center gap-1 mb-0.5">
                          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                            {label}
                          </p>
                          {changed && (
                            <span className="text-[8px] font-bold text-amber-600 bg-amber-100/80 px-1 py-[1px] rounded">
                              MODIFIED
                            </span>
                          )}
                        </div>
                        <p className={`text-xs font-medium ${changed ? "text-secondary" : "text-gray-700"}`}>
                          {format ? format(effectiveProposed[key]) : effectiveProposed[key] || "--"}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ─── Single Column (new product) ──────────────────── */
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr] gap-5 border border-gray-200 rounded-lg p-4">
            <div className="bg-[#FCFCFC] border border-gray-100 rounded-lg flex items-center justify-center p-4 min-h-[180px]">
              <img
                src={product.image || product.images?.[0] || "/placeholder-image.png"}
                alt={product.name}
                className="max-w-full max-h-[160px] object-contain"
              />
            </div>
            <div className="flex flex-col">
              <div className="mb-3">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Product Name</p>
                <h2 className="text-lg font-bold text-secondary">{product.name}</h2>
              </div>
              <div className="mb-4">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Description</p>
                <p className="text-gray-600 text-xs leading-relaxed">{product.description || "--"}</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-4">
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Category</p>
                  <p className="text-gray-800 font-medium">{product.type || "--"}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Price</p>
                  <p className="text-secondary font-bold">{formatPrice(product.price)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Quantity</p>
                  <p className="text-gray-800 font-medium">{product.quantity ?? "--"}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Measurement</p>
                  <p className="text-gray-800 font-medium">{product.measurement || "--"}</p>
                </div>
              </div>
              <div className="mt-auto bg-gray-50 rounded-md p-2.5 border border-gray-100 inline-block w-max">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Submitted By</p>
                <div className="flex items-center gap-2">
                  <div className="h-5 w-5 bg-gray-200 rounded-full flex items-center justify-center">
                    <FiUser className="text-gray-500 w-2.5 h-2.5" />
                  </div>
                  <div>
                    <p className="text-gray-800 font-medium text-xs">{submittedByName}</p>
                    {submittedByEmail && <p className="text-gray-400 text-[10px]">{submittedByEmail}</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Action Buttons (status-aware) ──────────────────── */}
        {adminLevel === "superadmin" && (
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
            <button
              onClick={() => onEdit(product)}
              className="text-[13px] text-secondary font-medium hover:underline flex items-center gap-1 transition-colors"
            >
              Edit Product Data
            </button>

            {canAction ? (
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={rejecting || approving}
                  className="bg-white border text-[13px] border-red-200 text-red-600 px-5 py-2 rounded-md font-semibold hover:bg-red-50 hover:border-red-300 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Reject
                </button>
                <button
                  onClick={handleApprove}
                  disabled={approving || rejecting}
                  className="bg-secondary text-[13px] text-white px-5 py-2 rounded-md font-semibold hover:bg-opacity-90 shadow-sm hover:shadow transition-all disabled:opacity-60 disabled:cursor-not-allowed border border-secondary"
                >
                  {approving ? "Approving..." : "Approve Product"}
                </button>
              </div>
            ) : (
              <span className={`text-xs font-semibold px-3 py-1.5 rounded-md border ${statusBadge.bg}`}>
                This product has been {statusBadge.label.toLowerCase()}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Rejection Reason Modal ──────────────────────────── */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => {
              if (!rejecting) {
                setShowRejectModal(false);
                setRejectionReason("");
              }
            }}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
              <div>
                <h3 className="text-sm font-bold text-gray-900">Reject Product</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Rejecting <span className="font-semibold text-gray-700">{product.name}</span>
                </p>
              </div>
              <button
                onClick={() => {
                  if (!rejecting) {
                    setShowRejectModal(false);
                    setRejectionReason("");
                  }
                }}
                className="p-1 rounded-md hover:bg-gray-100 transition-colors"
              >
                <FiX className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4">
              <label htmlFor="rejection-reason" className="block text-xs font-semibold text-gray-700 mb-1.5">
                Reason for rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                id="rejection-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this product is being rejected..."
                rows={4}
                className="w-full rounded-lg border border-gray-300 bg-gray-50/50 px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition resize-none"
                autoFocus
                disabled={rejecting}
              />
              <p className="text-[10px] text-gray-400 mt-1">
                This reason will be visible to the admin who submitted the product.
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2.5 px-5 py-3 border-t border-gray-100 bg-gray-50/50">
              <button
                type="button"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason("");
                }}
                disabled={rejecting}
                className="px-4 py-2 rounded-md text-[13px] font-medium text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRejectSubmit}
                disabled={rejecting || !rejectionReason.trim()}
                className="px-4 py-2 rounded-md text-[13px] font-semibold text-white bg-red-600 border border-red-600 hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {rejecting ? "Rejecting..." : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PendingProductDetails;
